import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ConsultationTreatmentTypes } from '@enums/consultation-treatment-types';
import { DataLayerCategories } from '@enums/data-layer-categories';
import { environment } from '@environments/environment';
import { NewConsultationRequestResponse } from '@models/consultation-request/new-consultation-request-response';
import { DataLayerResponse } from '@models/data-layer';
import { StorageService } from '@services/storage.service';
import { from, Observable } from 'rxjs';
import { concatMap, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
/**
 * Handles data layer requests.
 */
export class DataLayerService {
  private http = inject(HttpClient);
  private storageService = inject(StorageService);
  private document = inject<Document>(DOCUMENT);

  private window;

  public constructor() {
    this.window = this.document.defaultView;
  }

  /**
   * Push the Data Layer Category into the Ecommerce Data Layer Categories Tracking, preventing duplicates.
   *
   * @param {DataLayerCategories} dataLayerCategories data layer category to be pushed
   */
  public pushDataLayerTracking(dataLayerCategories: DataLayerCategories): void {
    const dataLayerTracking = this.storageService.ecommerceTrackingCategories;
    dataLayerTracking.push(dataLayerCategories);
    this.storageService.ecommerceTrackingCategories = [...new Set(dataLayerTracking)];
  }

  /**
   * Handle and set data layer structure
   */
  public handleDataLayer(): void {
    const transactionId = this.storageService.transactionId;

    if (!transactionId) {
      return;
    }

    if (!this.storageService.ecommerceTrackingCategories.length) {
      return;
    }

    from(this.storageService.ecommerceTrackingCategories)
      .pipe(concatMap((category: DataLayerCategories) => this.getDataLayer(transactionId, category)))
      .subscribe((dataLayer) => {
        this.window.dataLayer.push(dataLayer);
      });

    this.storageService.ecommerceTrackingCategories = [];
  }

  /**
   * Push element with email and phone information to the data layer.
   *
   * @param email email address to be stored in the datalayer
   * @param phone phone to be stored in the datalayer
   */
  public pushContactInfo(email: string, phone: string): void {
    this.window.dataLayer.push({ email: email, phone: phone });
  }

  /**
   * Push element with the information from the consultation request to the data layer.
   *
   * @param newConsultationRequest new consultation request response
   * @param treatmentType treatment type of the consultation
   * @param transactionId transaction id from the order
   */
  public addConsultationRequestPurchaseToDataLayer(
    newConsultationRequest: NewConsultationRequestResponse,
    treatmentType: ConsultationTreatmentTypes,
    transactionId: string,
  ): void {
    const dataLayerElement = {
      event: 'ecomm_event',
      transactionId: `${transactionId}_${treatmentType}_consultation`,
      transactionTotal: newConsultationRequest.payment_amount / 100,
      transactionProducts: newConsultationRequest.line_items
        .filter((item) => item.type !== 'Discount')
        .map((item) => ({
          sku: 'consultation',
          name: item.name,
          category: 'consultation',
          price: item.price / 100,
          quantity: 1,
        })),
    };

    if (this.window.dataLayer) {
      this.window.dataLayer.push(dataLayerElement);
    } else {
      this.window.dataLayer = [dataLayerElement];
    }
  }

  /**
   * Get the data layer data of the order.
   *
   * @param {string} transactionId transaction id of the order
   * @param {DataLayerCategories} category category to get the data layer
   */
  private getDataLayer(transactionId: string, category: DataLayerCategories): Observable<DataLayerResponse> {
    return this.http
      .get<DataLayerResponse>(
        `${environment.analyteCareApi}/api/v1/order-data-layer?transaction_id=${transactionId}&category=${category}`,
      )
      .pipe(
        map((dataLayerResponse) => {
          dataLayerResponse.transactionId =
            category === DataLayerCategories.Upsell
              ? dataLayerResponse.transactionId + '_upsell'
              : dataLayerResponse.transactionId;

          return dataLayerResponse;
        }),
      );
  }
}
